/**
 * SUBSCRIPTION GUARD SERVICE
 *
 * Serviço centralizado e seguro para controle de acesso baseado em assinatura.
 * Este serviço é responsável por determinar se um usuário tem acesso às
 * funcionalidades do app baseado no status da sua assinatura.
 *
 * Status de assinatura suportados:
 * - active: Assinatura ativa, acesso total
 * - paused: Assinatura pausada, sem acesso às funcionalidades
 * - cancelled: Assinatura cancelada, acesso até endDate
 * - refund_requested: Reembolso solicitado, sem acesso
 * - expired: Assinatura expirada, sem acesso
 * - trial: Período de trial, acesso limitado
 */

import { userService } from './user.js';
import { demoModeService } from './demo-mode.js';
import { t, localeService } from '../i18n/index.js';

class SubscriptionGuardService {
  constructor() {
    // Status que permitem acesso total ao app
    this.FULL_ACCESS_STATUS = ['active'];

    // Status que permitem acesso limitado (apenas páginas de gerenciamento)
    this.LIMITED_ACCESS_STATUS = ['paused', 'cancelled', 'refund_requested', 'expired', 'pending', 'suspended', 'pending_verification'];

    // Rotas sempre acessíveis (mesmo sem assinatura)
    this.PUBLIC_ROUTES = [
      '/',
      '/login',
      '/privacy-policy',
      '/terms-of-service',
      '/support',
      '/help-center',
      '/documentation',
      '/status',
      '/cookie-policy'
    ];

    // Rotas acessíveis para usuários autenticados sem assinatura ativa
    this.SUBSCRIPTION_MANAGEMENT_ROUTES = [
      '/checkout',
      '/subscription',
      '/upgrade',
      '/downgrade-selection',
      '/welcome',
      '/restricted',
      '/settings', // Settings acessível para gerenciamento de conta
      '/verify-email' // Verificação de email (pré-trial)
    ];

    // Rotas de clientes (acesso separado)
    this.CLIENT_ROUTES_PATTERN = /^\/clients\/(login|profile)\/[^\/]+$/;

    // Rotas acessíveis no modo demo (para usuários sem assinatura ativa)
    this.DEMO_MODE_ROUTES = [
      '/dashboard',
      '/clients'
    ];

    // Padrão para rotas de perfil de cliente no modo demo
    this.DEMO_CLIENT_PROFILE_PATTERN = /^\/clients\/[^\/]+$/;

    // Cache do status para evitar recálculos frequentes
    this._statusCache = null;
    this._statusCacheTime = 0;
    this._cacheTTL = 5000; // 5 segundos
  }

  /**
   * Obtém o status detalhado da assinatura do usuário atual
   * @returns {Object} Status detalhado da assinatura
   */
  getSubscriptionStatus() {
    const now = Date.now();

    // Usar cache se ainda válido
    if (this._statusCache && (now - this._statusCacheTime) < this._cacheTTL) {
      return this._statusCache;
    }

    const userData = userService.currentUser;

    if (!userData) {
      this._statusCache = {
        hasAccess: false,
        status: 'not_authenticated',
        reason: 'Usuário não autenticado',
        canAccessManagement: false,
        redirectTo: '/login'
      };
      this._statusCacheTime = now;
      return this._statusCache;
    }

    // Verificar se é um cliente (não agência)
    if (userData.role === 'client') {
      this._statusCache = {
        hasAccess: true,
        status: 'client',
        reason: 'Acesso de cliente',
        canAccessManagement: false,
        isClient: true,
        redirectTo: null
      };
      this._statusCacheTime = now;
      return this._statusCache;
    }

    // Membros da equipe herdam acesso da agência (subscription é do owner)
    if (userData.role === 'agency_member') {
      this._statusCache = {
        hasAccess: true,
        status: 'active',
        reason: 'Membro da equipe',
        canAccessManagement: true,
        redirectTo: null
      };
      this._statusCacheTime = now;
      return this._statusCache;
    }

    // Obter dados da assinatura
    const subscriptionStatus = userData.subscriptionStatus || 'none';
    const isPremium = userData.isPremium === true;
    const subscriptionEndDate = userData.subscriptionEndDate;
    const pauseUntil = userData.pauseUntil;

    // Calcular status real
    const status = this._calculateRealStatus(
      subscriptionStatus,
      isPremium,
      subscriptionEndDate,
      pauseUntil,
      userData.trialEndDate
    );

    this._statusCache = status;
    this._statusCacheTime = now;

    return status;
  }

  /**
   * Calcula o status real da assinatura considerando datas e condições
   * @private
   */
  _calculateRealStatus(subscriptionStatus, isPremium, endDate, pauseUntil, trialEndDate) {
    const now = new Date();

    // 1. Verificar se está pausada
    if (subscriptionStatus === 'paused') {
      return {
        hasAccess: false,
        status: 'paused',
        reason: 'Sua assinatura está pausada',
        canAccessManagement: true,
        redirectTo: '/subscription',
        pauseUntil: pauseUntil,
        message: pauseUntil
          ? `Assinatura pausada até ${this._formatDate(pauseUntil)}`
          : 'Assinatura pausada'
      };
    }

    // 1.5 Check if suspended (payment failure)
    if (subscriptionStatus === 'suspended') {
      return {
        hasAccess: false,
        status: 'suspended',
        reason: 'Assinatura suspensa por falha no pagamento',
        canAccessManagement: true,
        redirectTo: '/subscription',
        message: t('subscription.guard.suspended') || 'Assinatura suspensa. Atualize seu método de pagamento.'
      };
    }

    // 2. Verificar se tem reembolso solicitado - KEEP access during review
    if (subscriptionStatus === 'refund_requested') {
      return {
        hasAccess: true, // Keep access while refund is under review
        status: 'refund_pending',
        reason: 'Reembolso em analise',
        canAccessManagement: true,
        redirectTo: null,
        message: t('subscription.guard.refundPending') || 'Sua solicitacao de reembolso esta em analise. Voce mantem acesso ate a decisao.'
      };
    }

    // 3. Verificar se foi cancelada e o período expirou
    if (subscriptionStatus === 'cancelled' || subscriptionStatus === 'canceled') {
      if (endDate) {
        const endDateTime = new Date(endDate);
        if (now > endDateTime) {
          return {
            hasAccess: false,
            status: 'expired',
            reason: 'Assinatura expirada',
            canAccessManagement: true,
            redirectTo: '/subscription',
            expiredAt: endDate,
            message: `Sua assinatura expirou em ${this._formatDate(endDate)}`
          };
        } else {
          // Cancelada mas ainda no período pago
          return {
            hasAccess: true,
            status: 'cancelled_active',
            reason: 'Assinatura cancelada, mas ainda ativa',
            canAccessManagement: true,
            redirectTo: null,
            expiresAt: endDate,
            message: `Acesso até ${this._formatDate(endDate)}`
          };
        }
      }
      // Cancelada sem data de fim - bloquear
      return {
        hasAccess: false,
        status: 'cancelled',
        reason: 'Assinatura cancelada',
        canAccessManagement: true,
        redirectTo: '/subscription',
        message: t('subscription.guard.subscriptionCancelled')
      };
    }

    // 4. Verificar se está ativa
    if (subscriptionStatus === 'active' && isPremium) {
      // Verificar se a data de fim não passou (segurança extra)
      if (endDate) {
        const endDateTime = new Date(endDate);
        if (now > endDateTime) {
          // A data passou mas o status ainda está ativo - inconsistência
          // Isso não deveria acontecer, mas por segurança bloqueamos
          return {
            hasAccess: false,
            status: 'expired',
            reason: 'Período de assinatura expirado',
            canAccessManagement: true,
            redirectTo: '/subscription',
            message: t('subscription.guard.subscriptionExpired')
          };
        }
      }

      return {
        hasAccess: true,
        status: 'active',
        reason: 'Assinatura ativa',
        canAccessManagement: true,
        redirectTo: null,
        expiresAt: endDate,
        message: null
      };
    }

    // 4.5 Verificar pending_verification (signup sem email verificado)
    if (subscriptionStatus === 'pending_verification') {
      return {
        hasAccess: false,
        status: 'pending_verification',
        reason: 'Email não verificado',
        canAccessManagement: true,
        redirectTo: '/verify-email',
        message: t('subscription.guard.pendingVerification')
      };
    }

    // 5. Verificar trial
    if (subscriptionStatus === 'trial') {
      if (trialEndDate) {
        const trialEnd = new Date(trialEndDate);
        if (now > trialEnd) {
          return {
            hasAccess: false,
            status: 'trial_expired',
            reason: 'Período de teste expirado',
            canAccessManagement: true,
            redirectTo: '/checkout',
            message: t('subscription.guard.trialExpired')
          };
        }
      }
      return {
        hasAccess: true,
        status: 'trial',
        reason: 'Período de teste',
        canAccessManagement: true,
        redirectTo: null,
        trialEndsAt: trialEndDate,
        message: `Período de teste até ${this._formatDate(trialEndDate)}`
      };
    }

    // 6. Sem assinatura ou status desconhecido
    return {
      hasAccess: false,
      status: 'none',
      reason: 'Sem assinatura ativa',
      canAccessManagement: true,
      redirectTo: '/checkout',
      message: t('subscription.guard.noActiveSubscription')
    };
  }

  /**
   * Verifica se o usuário pode acessar uma rota específica
   * @param {string} path - Caminho da rota
   * @returns {Object} Resultado da verificação
   */
  canAccessRoute(path) {
    // Rotas públicas - sempre acessíveis
    if (this.PUBLIC_ROUTES.includes(path)) {
      return { allowed: true, redirectTo: null };
    }

    // Rotas de cliente - verificação separada
    if (this.CLIENT_ROUTES_PATTERN.test(path)) {
      return { allowed: true, redirectTo: null, isClientRoute: true };
    }

    const status = this.getSubscriptionStatus();

    // Usuário não autenticado
    if (status.status === 'not_authenticated') {
      return { allowed: false, redirectTo: '/login', reason: status.reason };
    }

    // Cliente tem acesso especial
    if (status.isClient) {
      // Clientes só podem acessar rotas de cliente
      if (path.startsWith('/clients/')) {
        return { allowed: true, redirectTo: null };
      }
      return { allowed: false, redirectTo: '/', reason: 'Acesso restrito a clientes' };
    }

    // Rotas de gerenciamento de assinatura - acessíveis se autenticado
    if (this.SUBSCRIPTION_MANAGEMENT_ROUTES.includes(path)) {
      return { allowed: true, redirectTo: null };
    }

    // Demais rotas - requerem assinatura ativa
    if (status.hasAccess) {
      return { allowed: true, redirectTo: null };
    }

    // MODO DEMO: Se o modo demo está ativo e o usuário não tem acesso,
    // permitir acesso às rotas de dashboard e clientes com dados mock
    if (demoModeService.getStatus()) {
      // Verificar se é uma rota permitida no modo demo
      if (this.DEMO_MODE_ROUTES.includes(path)) {
        return { allowed: true, redirectTo: null, isDemoMode: true };
      }

      // Verificar se é uma rota de perfil de cliente (ex: /clients/abc123)
      // Excluindo rotas de login/profile que já são tratadas separadamente
      if (this.DEMO_CLIENT_PROFILE_PATTERN.test(path) && !this.CLIENT_ROUTES_PATTERN.test(path)) {
        return { allowed: true, redirectTo: null, isDemoMode: true };
      }
    }

    // Sem acesso - redirecionar
    return {
      allowed: false,
      redirectTo: status.redirectTo || '/subscription',
      reason: status.reason,
      message: status.message
    };
  }

  /**
   * Verifica se o usuário tem acesso total ao app
   * @returns {boolean}
   */
  hasFullAccess() {
    const status = this.getSubscriptionStatus();
    return status.hasAccess === true;
  }

  /**
   * Verifica se o usuário pode gerenciar a assinatura
   * @returns {boolean}
   */
  canManageSubscription() {
    const status = this.getSubscriptionStatus();
    return status.canAccessManagement === true;
  }

  /**
   * Obtém mensagem de status para exibir ao usuário
   * @returns {string|null}
   */
  getStatusMessage() {
    const status = this.getSubscriptionStatus();
    return status.message || null;
  }

  /**
   * Obtém o motivo do bloqueio (se houver)
   * @returns {string|null}
   */
  getBlockReason() {
    const status = this.getSubscriptionStatus();
    if (status.hasAccess) return null;
    return status.reason;
  }

  /**
   * Limpa o cache de status (usar após atualização de assinatura)
   */
  clearCache() {
    this._statusCache = null;
    this._statusCacheTime = 0;
  }

  /**
   * Formata data para exibição
   * @private
   */
  _formatDate(dateString) {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString(localeService.getLocale(), {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  }

  /**
   * Verifica se uma feature específica está disponível no plano atual
   * @param {string} feature - Nome da feature
   * @returns {boolean}
   */
  hasFeature(feature) {
    const status = this.getSubscriptionStatus();
    if (!status.hasAccess) return false;

    const userData = userService.currentUser;
    const tier = userData?.subscriptionTier || 'basic';

    // Features por tier
    const tierFeatures = {
      basic: ['meta_ads', 'google_ads', 'agency_dashboard', 'email_support'],
      professional: ['meta_ads', 'google_ads', 'agency_dashboard', 'email_support', 'client_portal', 'pdf_export', 'excel_export', 'priority_support'],
      enterprise: ['meta_ads', 'google_ads', 'agency_dashboard', 'email_support', 'client_portal', 'pdf_export', 'excel_export', 'priority_support', 'white_label', 'api_access', 'dedicated_support'],
      ondemand: ['meta_ads', 'google_ads', 'agency_dashboard', 'email_support', 'client_portal', 'pdf_export', 'excel_export', 'priority_support', 'white_label', 'api_access', 'dedicated_support']
    };

    const features = tierFeatures[tier] || tierFeatures.basic;
    return features.includes(feature);
  }

  /**
   * Obtém o limite de clientes baseado no plano contratado.
   *
   * IMPORTANTE: O limite é baseado no último plano contratado e permanece
   * válido até a data de expiração (subscriptionEndDate), mesmo em caso de:
   * - Cancelamento após período de reembolso
   * - Status 'cancelled_active' (cancelado mas ainda no período pago)
   *
   * O limite só é zerado quando:
   * - O período contratado expira (status 'expired')
   * - Há reembolso (status 'refunded')
   * - Assinatura é suspensa por falha de pagamento
   *
   * @returns {number} Limite de clientes do plano
   */
  getClientLimit() {
    const status = this.getSubscriptionStatus();
    const userData = userService.currentUser;

    // Sem acesso = sem limite de clientes
    // Isso inclui: expired, refunded, suspended, not_authenticated
    if (!status.hasAccess) {
      return 0;
    }

    // Para status 'cancelled_active': usuário cancelou mas ainda está no período pago
    // Deve manter o limite do plano contratado até subscriptionEndDate
    // O maxClients salvo no momento da contratação é preservado

    // Primeiro verifica maxClients salvo diretamente (atualizado pelo backend no momento da contratação)
    // Este valor NÃO é alterado no cancelamento, apenas no reembolso ou expiração
    if (userData?.maxClients) {
      return userData.maxClients;
    }

    // Fallback: calcular baseado no tier da última assinatura
    const tier = userData?.subscriptionTier || 'basic';
    const limits = {
      basic: 5,
      professional: 15,
      enterprise: 30,
      ondemand: userData?.customClientCount || userData?.maxClients || 31
    };

    return limits[tier] || 5;
  }

  /**
   * Verifica se o usuário está em período de graça (cancelou mas ainda pode usar)
   * @returns {Object} { inGracePeriod: boolean, expiresAt: string|null, daysRemaining: number }
   */
  getGracePeriodInfo() {
    const status = this.getSubscriptionStatus();

    if (status.status !== 'cancelled_active') {
      return { inGracePeriod: false, expiresAt: null, daysRemaining: 0 };
    }

    const expiresAt = status.expiresAt;
    if (!expiresAt) {
      return { inGracePeriod: true, expiresAt: null, daysRemaining: 0 };
    }

    const now = new Date();
    const expDate = new Date(expiresAt);
    const diffTime = expDate.getTime() - now.getTime();
    const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return {
      inGracePeriod: true,
      expiresAt,
      daysRemaining: Math.max(0, daysRemaining)
    };
  }

  /**
   * Obtém informações detalhadas da assinatura para exibição
   * @returns {Object} Detalhes da assinatura
   */
  getSubscriptionDetails() {
    const status = this.getSubscriptionStatus();
    const userData = userService.currentUser;
    const gracePeriod = this.getGracePeriodInfo();

    return {
      // Status atual
      status: status.status,
      hasAccess: status.hasAccess,
      message: status.message,

      // Informações do plano
      tier: userData?.subscriptionTier || 'none',
      maxClients: this.getClientLimit(),

      // Datas importantes
      subscriptionEndDate: userData?.subscriptionEndDate || null,

      // Período de graça (para cancelamentos)
      gracePeriod,

      // Flags úteis
      isActive: status.status === 'active',
      isCancelled: status.status === 'cancelled_active' || status.status === 'cancelled',
      isExpired: status.status === 'expired',
      willExpire: status.status === 'cancelled_active',

      // Mensagem amigável
      displayMessage: this._getDisplayMessage(status, gracePeriod)
    };
  }

  /**
   * Gera mensagem amigável para exibição ao usuário
   * @private
   */
  _getDisplayMessage(status, gracePeriod) {
    switch (status.status) {
      case 'active':
        return 'Sua assinatura está ativa';
      case 'cancelled_active':
        if (gracePeriod.daysRemaining > 0) {
          return `Assinatura cancelada. Acesso válido por mais ${gracePeriod.daysRemaining} dia${gracePeriod.daysRemaining > 1 ? 's' : ''}`;
        }
        return 'Assinatura cancelada. Acesso expira em breve';
      case 'expired':
        return 'Sua assinatura expirou. Renove para continuar usando';
      case 'paused':
        return 'Sua assinatura está pausada';
      case 'refund_requested':
        return 'Reembolso solicitado. Acesso suspenso';
      default:
        return status.message || 'Sem assinatura ativa';
    }
  }

  /**
   * Verifica se pode adicionar mais clientes
   * @param {number} currentCount - Número atual de clientes
   * @returns {boolean}
   */
  canAddClient(currentCount = 0) {
    const limit = this.getClientLimit();
    return currentCount < limit;
  }

  /**
   * Verifica se o usuário excede o limite de clientes do plano
   * Usado para redirecionar para página de seleção de clientes
   *
   * IMPORTANTE: Para usuários com assinatura cancelada mas ainda no período pago,
   * o limite permanece o do plano contratado até a data de expiração.
   *
   * @param {number} activeClientsCount - Número de clientes ativos
   * @returns {Object} Informações sobre limite de clientes
   */
  checkClientLimitExceeded(activeClientsCount = 0) {
    const limit = this.getClientLimit();
    const status = this.getSubscriptionStatus();
    const gracePeriod = this.getGracePeriodInfo();

    return {
      exceedsLimit: activeClientsCount > limit,
      limit,
      activeCount: activeClientsCount,
      excess: Math.max(0, activeClientsCount - limit),

      // Informações adicionais para UI
      subscriptionStatus: status.status,
      inGracePeriod: gracePeriod.inGracePeriod,
      gracePeriodDaysRemaining: gracePeriod.daysRemaining,
      expiresAt: gracePeriod.expiresAt,

      // Mensagem para exibição
      message: this._getLimitMessage(activeClientsCount, limit, status, gracePeriod)
    };
  }

  /**
   * Gera mensagem sobre limite de clientes
   * @private
   */
  _getLimitMessage(activeCount, limit, status, gracePeriod) {
    if (status.status === 'cancelled_active' && gracePeriod.daysRemaining > 0) {
      return `${activeCount}/${limit} clientes (acesso válido por mais ${gracePeriod.daysRemaining} dias)`;
    }

    if (activeCount > limit) {
      return `${activeCount}/${limit} clientes - Limite excedido`;
    }

    return `${activeCount}/${limit} clientes`;
  }

  /**
   * Verifica se deve redirecionar para seleção de clientes
   * @param {number} activeClientsCount - Número de clientes ativos
   * @returns {boolean}
   */
  shouldRedirectToClientSelection(activeClientsCount = 0) {
    const { exceedsLimit } = this.checkClientLimitExceeded(activeClientsCount);
    return exceedsLimit;
  }
}

// Exportar instância singleton
export const subscriptionGuard = new SubscriptionGuardService();
